<!--
Tooltips have a delay before they show up, to avoid annoying the user when moving the mouse around.
This delay cannot be implemented in CSS alone, because we need to delay the "show" state being set. If we would do
it with CSS only, the tooltip would be added to DOM immediately, and register listener etc., which is not desired if
the user only quickly moves the mouse over the element.
-->
<svelte:options
  customElement={{
    tag: "oc-popover-v1",

    extend: window.__components.extend(),
    props: {
      position: { type: "String", reflect: true },
      anchor: { type: "String" },
      trigger: { type: "String" },
      closeOn: { type: "String", attribute: "close-on" },
      visible: { type: "Boolean", reflect: true },
      sticky: { type: "Boolean", reflect: true },
      closeButton: { type: "Boolean", attribute: "close-button" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
    },
  }}
/>

<script lang="ts">
  import { on } from "svelte/events";
  import { nanoid } from "@otto-ec/global-resources/misc";
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import { unsetAll } from "@otto-ec/global-resources/event";
  import { focusDeepWithin } from "@otto-ec/otto-components-utils/utils/focus/focusDeepWithin";
  import { OcPopoverTrigger, OcPopoverVariant, type Events, type Props } from "./PopoverV1.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import Popover from "../../../common/components/Popover/Popover.svelte";
  import { PopoverPosition } from "../../../common/components/Popover/Popover.types";
  import Bubble from "../../../common/components/Popover/Bubble.svelte";
  import { getTokenChecker, type TokenChecker } from "./Popover.utils";
  import { longPressEvent } from "../../../common/actions/longPressEvent";

  const {
    position,
    anchor,
    variant = OcPopoverVariant.FLYOUT,
    trigger,
    closeOn,
    closeButton,
    visible = false,
    sticky = false,
    ocAriaLabel = undefined,
  }: Props = $props();

  const Host = $host();
  const anchorName = `--popover-${nanoid(7)}`;

  const dispatch = useEventDispatcher<Events>(Host);

  let anchorElement = $derived.by<HTMLElement | null>(() => {
    if (!anchor) return null;
    if (anchor === "previous-sibling") {
      /*                                                                                          */
      const prevSibling = Host.previousElementSibling;
      if (prevSibling instanceof HTMLElement) return prevSibling;
      return null;
    }
    const element =
      typeof anchor == "string" ? (document.querySelector(anchor) as HTMLElement) : anchor;
    return element;
  });

  let popoverElement = $state<Popover>();
  let popoverContentElement = $state<HTMLElement>();

  /*                                                    */
  let hasTrigger = $derived.by<TokenChecker<OcPopoverTrigger>>(() => {
    return getTokenChecker(
      (() => {
        if (trigger) return trigger;
        if (variant === OcPopoverVariant.FLYOUT) return OcPopoverTrigger.CLICK;
        if (variant === OcPopoverVariant.TOGGLETIP) return OcPopoverTrigger.CLICK;
        if (variant === OcPopoverVariant.TOOLTIP)
          return `${OcPopoverTrigger.MOUSEENTER} ${OcPopoverTrigger.LONGPRESS} ${OcPopoverTrigger.FOCUS}`;
        return OcPopoverTrigger.NONE;
      })(),
    );
  });
  let hasCloseOn = $derived.by<TokenChecker<OcPopoverTrigger>>(() => {
    return getTokenChecker(
      (() => {
        if (closeOn) return closeOn;
        const closeOns = [];
        if (hasTrigger(OcPopoverTrigger.CLICK)) closeOns.push(OcPopoverTrigger.CLICK);
        if (hasTrigger(OcPopoverTrigger.MOUSEENTER)) closeOns.push(OcPopoverTrigger.MOUSELEAVE);
        if (hasTrigger(OcPopoverTrigger.LONGPRESS)) closeOns.push(OcPopoverTrigger.CLICK);
        if (hasTrigger(OcPopoverTrigger.FOCUS)) closeOns.push(OcPopoverTrigger.BLUR);
        return closeOns.length > 0 ? closeOns.join(" ") : OcPopoverTrigger.NONE;
      })(),
    );
  });

  /*                                                                     */
  let focusReturn: HTMLElement | null = null;

  let moveFocusToButton = false;
  function handleFullyShown(): void {
    if (moveFocusToButton) {
      /*                                                                      */
      /*                                                  */
      popoverContentElement?.focus();
      moveFocusToButton = false;
    }
  }

  /*                                         */
  /*                                     */
  let show = $state<boolean>(visible);
  let showDelayTimeout: ReturnType<typeof setTimeout> | null = null;

  function open(target: EventTarget | null, manageFocus: boolean): void {
    if (show || showDelayTimeout) return;
    showDelayTimeout = setTimeout(
      () => {
        if (!dispatch("oc-popover-open", undefined, { alias: ["oc-open"] })) return;
        show = true;

        if (!manageFocus) return;
        if (target instanceof HTMLElement) focusReturn = target;
        /*                                             */
        moveFocusToButton = true;
      },
      variant === OcPopoverVariant.TOOLTIP ? 500 : 0,
    ); /*                                                                                          */
  }
  function close(manageFocus: boolean): void {
    if (showDelayTimeout) {
      clearTimeout(showDelayTimeout);
      showDelayTimeout = null;
    }
    if (!show) return;
    if (!dispatch("oc-popover-close", undefined, { alias: ["oc-close"] })) return;
    show = false;
    moveFocusToButton = false;
    if (manageFocus && focusReturn) focusDeepWithin(focusReturn);
    focusReturn = null;
  }

  function handleCloseButtonClick(evt: MouseEvent): void {
    if (!(evt.target instanceof HTMLElement)) return;
    if (!(evt.target.dataset.popoverClose == "click")) return;
    close(evt.detail === 0);
  }

  /*                                                            */
  function handleContentKeyup(evt: KeyboardEvent): void {
    /*                                                             */
    if (!hasCloseOn(OcPopoverTrigger.CLICK)) return;
    if (evt.key === "Escape" && show) close(true);
  }

  /*                                                                      */
  let isInnerBoxClick = false;
  function handleBoxClick(): void {
    isInnerBoxClick = true;
  }
  const handleDocumentClick = (): void => {
    if (!isInnerBoxClick && hasCloseOn(OcPopoverTrigger.CLICK)) close(false);
    isInnerBoxClick = false;
  };

  /*                                                                */
  let prevVisibleProp = false;
  $effect(() => {
    if (visible === prevVisibleProp) return;
    prevVisibleProp = visible;
    if (visible) {
      open(null, false);
    } else {
      close(false);
    }
  });

  $effect(() => {
    $host().classList.toggle("oc-popover__variant-flyout", variant === OcPopoverVariant.FLYOUT);
    $host().classList.toggle(
      "oc-popover__variant-toggletip",
      variant === OcPopoverVariant.TOGGLETIP,
    );
    $host().classList.toggle("oc-popover__variant-tooltip", variant === OcPopoverVariant.TOOLTIP);
  });

  /*                                                     */
  $effect(() => {
    if (!anchorElement) return;

    const currentAnchor = anchorElement;

    Host.style.setProperty("--anchor", anchorName);

    /*                                                                                          */
    const existingAnchorNames = currentAnchor.style.getPropertyValue("anchor-name").trim();
    const anchorNames = existingAnchorNames ? `${existingAnchorNames}, ${anchorName}` : anchorName;
    currentAnchor.style.setProperty("anchor-name", anchorNames);

    return () => {
      const currentAnchorNames = currentAnchor.style
        .getPropertyValue("anchor-name")
        .split(/\s*,\s*/)
        .filter((name) => name && name !== anchorName)
        .join(", ");
      if (currentAnchorNames) {
        currentAnchor.style.setProperty("anchor-name", currentAnchorNames);
      } else {
        currentAnchor.style.removeProperty("anchor-name");
      }
    };
  });

  /*                                         */
  $effect(() => {
    if (!anchorElement) return;
    if (!hasTrigger(OcPopoverTrigger.CLICK) && !hasCloseOn(OcPopoverTrigger.CLICK)) return;
    return on(anchorElement, "click", (evt: MouseEvent): void => {
      if (show) {
        if (!hasCloseOn(OcPopoverTrigger.CLICK)) return;
        close(true);
      } else {
        if (!hasTrigger(OcPopoverTrigger.CLICK)) return;
        /*                                                                            */
        isInnerBoxClick = true;
        open(evt.currentTarget, true);
      }
    });
  });

  $effect(() => {
    if (!anchorElement) return;
    if (!hasCloseOn(OcPopoverTrigger.CLICK)) return;
    return on(anchorElement, "keyup", handleContentKeyup);
  });

  $effect(() => {
    if (!anchorElement) return;
    if (!hasTrigger(OcPopoverTrigger.MOUSEENTER)) return;
    return on(anchorElement, "mouseover", (): void => {
      open(anchorElement, false);
    });
  });

  $effect(() => {
    if (!anchorElement) return;
    if (!hasTrigger(OcPopoverTrigger.MOUSEENTER)) return;
    return on(anchorElement, "focusin", (): void => {
      open(anchorElement, false);
    });
  });

  $effect(() => {
    if (!anchorElement) return;
    if (!hasTrigger(OcPopoverTrigger.LONGPRESS)) return;

    return unsetAll(
      longPressEvent(anchorElement, 500).destroy,
      on(anchorElement, "longpress", (): void => {
        open(anchorElement, false);
      }),
    );
  });

  $effect(() => {
    if (!anchorElement) return;
    if (!hasCloseOn(OcPopoverTrigger.MOUSELEAVE)) return;
    return on(anchorElement, "mouseout", (): void => {
      close(false);
    });
  });

  $effect(() => {
    if (!anchorElement) return;
    if (!hasCloseOn(OcPopoverTrigger.MOUSELEAVE)) return;
    return on(anchorElement, "focusout", (): void => {
      close(false);
    });
  });

  let preferredPosition = $derived(position as PopoverPosition);

  export function recalcPosition(): void {
    popoverElement?.recalcPosition();
  }
</script>

<!-- <svelte:window onresize={() => close(true)} /> -->
<svelte:document onclick={handleDocumentClick} />
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="popover-box" onclick={handleBoxClick}>
  {#if anchorElement !== null}
    <Popover
      {show}
      {sticky}
      {preferredPosition}
      onFullyShown={handleFullyShown}
      bind:this={popoverElement}
      contextElement={anchorElement}
      bubble={Bubble}
    >
      <div
        role="dialog"
        tabindex="-1"
        aria-label={ocAriaLabel}
        onkeyup={handleContentKeyup}
        onclick={handleCloseButtonClick}
        bind:this={popoverContentElement}
      >
        {#if closeButton}
          <span class="close-button-wrapper">
            <oc-icon-button-v3
              class="close-button"
              variant="transparent-inverted"
              icon="close"
              oc-aria-label="Schließen"
              data-popover-close="click"
            >
            </oc-icon-button-v3>
          </span>
        {/if}
        <slot />
      </div>
    </Popover>
  {/if}
</div>

<DefaultStyles display="block" />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  :global {
    :host {
      position: absolute;

      --min-width: initial;
      --max-width: initial;
      --arrow-height: initial;
      --background-color: initial;
      --border-radius: initial;
      --padding: initial;
    }
    :host(.oc-popover__variant-flyout) {
      /*                      */
      --popover-min-width: var(--min-width, 0);
      --popover-max-width: var(--max-width, #{tokens.$oc-component-popover-default-max-width});
      --bubble-arrow-height: var(
        --arrow-height,
        #{tokens.$oc-component-popover-default-arrow-height}
      );
      --bubble-background-color: var(
        --background-color,
        #{tokens.$oc-component-popover-default-background-color}
      );
      --bubble-border-radius: var(
        --border-radius,
        #{tokens.$oc-component-popover-default-border-radius}
      );
      --bubble-padding: var(--padding, 0px);
      /*              */
      --popover-frame-spacing: #{tokens.$oc-component-popover-frame-spacing};
      --popover-transition-show:
        opacity #{tokens.$oc-component-popover-show-duration}
          #{tokens.$oc-component-popover-show-easing} 0.05s,
        visibility 0ms;
      --popover-transition-hide:
        opacity #{tokens.$oc-component-popover-hide-duration}
          #{tokens.$oc-component-popover-hide-easing} 0s,
        visibility 0ms #{tokens.$oc-component-popover-hide-duration};
      /*                                      */
      --bubble-copy-color: #{tokens.$oc-semantic-color-text-default};
      --bubble-context-spacing: #{tokens.$oc-component-popover-context-offset};
      --bubble-arrow-width: calc(var(--bubble-arrow-height) * 2);
      --bubble-copy-font: #{tokens.$oc-semantic-font-copy-100};
      --bubble-shadow-filter: #{tokens.$oc-component-popover-shadow};
    }

    :host(.oc-popover__variant-toggletip) {
      /*                      */
      --popover-min-width: var(--min-width, 0);
      --popover-max-width: var(--max-width, #{tokens.$oc-component-toggletip-max-width});
      --bubble-arrow-height: var(--arrow-height, #{tokens.$oc-component-toggletip-arrow-height});
      --bubble-background-color: var(
        --background-color,
        #{tokens.$oc-component-toggletip-background-color}
      );
      --bubble-padding: var(--padding, 12px);
      --bubble-border-radius: var(--border-radius, #{tokens.$oc-component-toggletip-border-radius});

      /*                                                             */
      --popover-frame-spacing: #{tokens.$oc-component-toggletip-frame-spacing};
      --popover-transition-show:
        opacity #{tokens.$oc-component-toggletip-show-duration}
          #{tokens.$oc-component-toggletip-show-easing} 0.05s,
        visibility 0ms #{tokens.$oc-component-toggletip-show-duration};
      --popover-transition-hide:
        opacity #{tokens.$oc-component-toggletip-hide-duration}
          #{tokens.$oc-component-toggletip-hide-easing} 0s,
        visibility 0ms #{tokens.$oc-component-toggletip-hide-duration};
      /*                                      */
      --bubble-copy-color: #{tokens.$oc-component-toggletip-copy-color};
      --bubble-copy-font: #{tokens.$oc-component-toggletip-copy-font};
      --bubble-arrow-width: calc(var(--bubble-arrow-height) * 2);
      --bubble-context-spacing: #{tokens.$oc-component-toggletip-context-spacing};
      --bubble-shadow-filter: #{tokens.$oc-component-toggletip-shadow};
    }
    :host(.oc-popover__variant-tooltip) {
      --popover-min-width: var(--min-width, 0);
      --popover-max-width: var(--max-width, #{tokens.$oc-component-tooltip-max-width});
      --bubble-arrow-height: var(--arrow-height, #{tokens.$oc-component-tooltip-arrow-height});
      --bubble-background-color: var(
        --background-color,
        #{tokens.$oc-component-tooltip-background-color}
      );
      --bubble-border-radius: var(--border-radius, #{tokens.$oc-component-tooltip-border-radius});
      --bubble-padding: var(
        --padding,
        #{tokens.$oc-component-tooltip-spacing-y} #{tokens.$oc-component-tooltip-spacing-x}
      );

      /*                                                             */
      --popover-frame-spacing: #{tokens.$oc-component-tooltip-frame-spacing};
      --popover-transition-show: opacity #{tokens.$oc-component-tooltip-show-duration}
        #{tokens.$oc-component-tooltip-show-easing} 0.05s; /*                                                                                      */
      --popover-transition-hide: opacity #{tokens.$oc-component-tooltip-hide-duration}
        #{tokens.$oc-component-tooltip-hide-easing} #{tokens.$oc-component-tooltip-hide-delay};
      --bubble-copy-color: #{tokens.$oc-component-tooltip-copy-color};
      --bubble-copy-font: #{tokens.$oc-component-tooltip-copy-font};
      --bubble-arrow-width: calc(var(--bubble-arrow-height) * 2);
      --bubble-context-spacing: #{tokens.$oc-component-tooltip-context-spacing};
      --bubble-shadow-filter: #{tokens.$oc-component-tooltip-shadow};
    }

    .close-button-wrapper {
      display: block;
      float: right;
      margin: -4px -4px -4px tokens.$oc-component-toggletip-gap-x;
    }
  }
</style>
